import {
	ColumnDef,
	OnChangeFn,
	SortingState,
	flexRender,
	getCoreRowModel,
	getSortedRowModel,
	useReactTable
} from "@tanstack/react-table";
import { Constants } from "appConstants";
import { IcoArrowDown, IcoArrowUp, IcoReverter } from "assets/icons";
import clsx from "clsx";
import Pagination, { PaginationProps } from "components/Pagination";
import "./styles.scss";

export type TableProps<TData> = {
	columns: ColumnDef<TData>[];
	data: TData[];
	sorting?: SortingState;
	setSorting?: OnChangeFn<SortingState>;
	onClickRow?: (id: string) => void;
} & PaginationProps;

type ColumnMetaProps = {
	columnClass: string;
	headerClass: string;
	cellClass: string;
	footerClass: string;
};

export default function Table({
	columns,
	data,
	sorting,
	setSorting,
	numberOfInstancesOptions,
	totalPages,
	currentPage,
	onPageChange,
	pageSize,
	cssCompact,
	onClickRow
}: Readonly<TableProps<any>>) {
	const { getHeaderGroups, getRowModel, getFooterGroups, getAllColumns } =
		useReactTable({
			data,
			columns,
			defaultColumn: {
				size: 100 / columns.length
			},
			getCoreRowModel: getCoreRowModel(),
			...(setSorting && { onSortingChange: setSorting }),
			getSortedRowModel: getSortedRowModel(),
			manualSorting: true,
			state: {
				...(sorting && { sorting })
			}
		});

	return (
		<>
			<table className="custom-table">
				<thead>
					{getHeaderGroups().map((headerGroup) => (
						<tr key={headerGroup.id}>
							{headerGroup.headers.map((header) => (
								<th
									key={header.id}
									className={clsx({
										"header-sortable": header.column.getCanSort(),
										[(header.column.columnDef.meta as ColumnMetaProps)
											?.columnClass]: !!(
											header.column.columnDef.meta as ColumnMetaProps
										)?.columnClass,
										[(header.column.columnDef.meta as ColumnMetaProps)
											?.headerClass]: !!(
											header.column.columnDef.meta as ColumnMetaProps
										)?.headerClass
									})}
									style={{ width: `${header.getSize()}%` }}
									onClick={(e) => {
										const sortingHandler =
											header.column.getToggleSortingHandler();
										if (sortingHandler) sortingHandler(e);
									}}
								>
									{header.isPlaceholder
										? null
										: flexRender(
												header.column.columnDef.header,
												header.getContext()
										  )}
									{header.column.getCanSort() && (
										<span className="sort-icon">
											{{
												asc: <IcoArrowUp size="12" />,
												desc: <IcoArrowDown size="12" />
											}[header.column.getIsSorted() as string] ?? (
												<IcoReverter />
											)}
										</span>
									)}
								</th>
							))}
						</tr>
					))}
				</thead>
				<tbody>
					{data ? (
						data.length > 0 ? (
							getRowModel().rows.map((row) =>
								!row.original.isChildOfExpanded ? (
									<tr
										key={row.id}
										className={onClickRow ? "cursor-pointer" : undefined}
										onClick={() =>
											onClickRow ? onClickRow(row?.original?.id) : undefined
										}
									>
										{row.getVisibleCells().map((cell) => (
											<td
												key={cell.id}
												className={
													`${
														(cell.column.columnDef.meta as ColumnMetaProps)
															?.columnClass || ""
													}` +
													` ${
														(cell.column.columnDef.meta as ColumnMetaProps)
															?.cellClass || ""
													}`
												}
											>
												{flexRender(
													cell.column.columnDef.cell,
													cell.getContext()
												)}
											</td>
										))}
									</tr>
								) : (
									<tr key={1}>
										<td key={1} colSpan={getAllColumns().length}>
											{Constants.strings.pages.listing.messages.error}
										</td>
									</tr>
								)
							)
						) : (
							<tr>
								<td colSpan={columns.length}>
									{Constants.strings.pages.listing.messages.noData}
								</td>
							</tr>
						)
					) : (
						<tr key={1}>
							<td
								key={1}
								colSpan={getAllColumns().length}
								className="text-feedback-negative-pure-500"
							>
								{Constants.strings.pages.listing.messages.error}
							</td>
						</tr>
					)}
				</tbody>
				<tfoot>
					{getFooterGroups().map((footerGroup) => (
						<tr key={footerGroup.id}>
							{footerGroup.headers.map((header) => (
								<th
									key={header.id}
									className={
										`${
											(header.column.columnDef.meta as ColumnMetaProps)
												?.columnClass || ""
										}` +
										` ${
											(header.column.columnDef.meta as ColumnMetaProps)
												?.footerClass || ""
										}`
									}
								>
									{header.isPlaceholder
										? null
										: flexRender(
												header.column.columnDef.footer,
												header.getContext()
										  )}
								</th>
							))}
						</tr>
					))}
				</tfoot>
			</table>
			<Pagination
				numberOfInstancesOptions={numberOfInstancesOptions}
				totalPages={totalPages}
				currentPage={currentPage}
				onPageChange={onPageChange}
				pageSize={pageSize}
				cssCompact={cssCompact}
			/>
		</>
	);
}
